import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Eye, AlertTriangle, RotateCcw, Trash2, BookOpen, Bell } from "lucide-react";
import { EnablePushButton } from "@/components/EnablePushButton";
import { useTranslation } from "react-i18next";
import { Navbar } from "@/components/Navbar";
import { UserGuide } from "@/components/UserGuide";
import { InstallAppButton } from "@/components/InstallAppButton";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { usePro } from "@/contexts/ProContext";
import { LANGUAGES } from "@/lib/i18n";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ACCENT_COLORS, CURRENCIES } from "@/lib/api";
import { toast } from "sonner";

const EMOJIS = ["🙂", "😎", "🧑", "👩", "🧔", "👨", "🧕", "🧑‍🎤", "🧑‍💻", "🦊", "🐱", "🐶"];

export default function Settings() {
  const { user, updateUser, logout } = useAuth();
  const { theme, accent, setTheme, setAccent } = useTheme();
  const { language, setLanguage, privacyMode, setPrivacyMode } = usePro();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [emoji, setEmoji] = useState(user?.avatar_emoji || "🙂");
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deletingAcct, setDeletingAcct] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [nudgesEnabled, setNudgesEnabled] = useState(user?.smart_nudges_enabled ?? user?.is_pro ?? false);
  const toggleNudges = async (val) => {
    setNudgesEnabled(val);
    try { await api.patch("/users/me/smart-nudges", { enabled: val }); }
    catch { toast.error("Failed"); setNudgesEnabled(!val); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateUser({ name, avatar_emoji: emoji, currency });
      toast.success(t("common.saved"));
    } catch {
      toast.error(t("common.failed"));
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    if (resetText !== "RESET") { toast.error("Type RESET to confirm"); return; }
    setResetting(true);
    try {
      await api.post("/account/reset");
      toast.success("All data cleared. Starting fresh ✨");
      setResetOpen(false);
      setResetText("");
      setTimeout(() => { window.location.href = "/dashboard"; }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setResetting(false); }
  };

  const doDelete = async () => {
    if (deleteText !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setDeletingAcct(true);
    try {
      await api.delete("/account");
      toast.success("Account deleted");
      logout();
      setTimeout(() => nav("/"), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setDeletingAcct(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-2">/ Settings</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-10">Your preferences</h1>

        <div className="space-y-10">
          {/* Profile */}
          <section className="rounded-2xl border border-border bg-card/40 p-6" data-testid="settings-profile">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-5">Profile</div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-muted border-border" data-testid="settings-name" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Avatar</Label>
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map((e) => (
                    <button
                      key={e} onClick={() => setEmoji(e)}
                      className={`h-10 w-10 rounded-md text-lg transition ${emoji === e ? "bg-foreground/10 ring-1 ring-[var(--brand)]" : "bg-foreground/[0.03] hover:bg-foreground/10"}`}
                      data-testid={`avatar-${e}`}
                    >{e}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">Default currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="mt-1.5 bg-muted border-border" data-testid="settings-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.symbol} {c.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} disabled={saving} className="brand-bg text-black hover:opacity-90" data-testid="settings-save-btn">
                {saving ? "Saving…" : "Save profile"}
              </Button>
            </div>
          </section>

          {/* Appearance */}
          <section className="rounded-2xl border border-border bg-card/40 p-6" data-testid="settings-appearance">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/40 mb-5">Appearance</div>
            <div className="space-y-6">
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Theme</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "oled", label: "OLED Black" },
                    { value: "dark", label: "Dark" },
                    { value: "light", label: "Light" },
                  ].map((t) => (
                    <button
                      key={t.value} onClick={() => setTheme(t.value)}
                      className={`px-4 h-10 rounded-md border text-sm transition ${theme === t.value ? "brand-border text-foreground bg-foreground/5" : "border-border text-foreground/60 hover:text-white"}`}
                      data-testid={`theme-${t.value}`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50 mb-2 block">Accent color</Label>
                <div className="flex flex-wrap gap-2">
                  {ACCENT_COLORS.map((c) => (
                    <button
                      key={c.value} onClick={() => setAccent(c.value)}
                      className={`h-10 w-10 rounded-md border-2 transition ${accent === c.value ? "border-white" : "border-transparent"}`}
                      style={{ background: c.value }}
                      title={c.label}
                      data-testid={`accent-${c.label.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Preferences */}
          <section className="rounded-2xl border border-border bg-card/40 p-6" data-testid="settings-preferences">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-5">Preferences</div>
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-sm">{t("nav.language")}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {LANGUAGES.find((l) => l.code === language)?.native || language}
                    </div>
                  </div>
                </div>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-48 bg-muted border-border" data-testid="settings-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-80">
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code} data-testid={`settings-lang-${l.code}`}>
                        {l.native} <span className="text-muted-foreground ml-2 text-xs">{l.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <Eye className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-sm">{t("privacy.label")}</div>
                    <div className="text-xs text-muted-foreground">Blur all amounts</div>
                  </div>
                </div>
                <Switch checked={privacyMode} onCheckedChange={setPrivacyMode} data-testid="settings-privacy-switch" />
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                <div className="flex items-center gap-3 min-w-0">
                  <Bell className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-sm">Push notifications</div>
                    <div className="text-xs text-muted-foreground">Get notified about bills, friend requests & invites — even when the app is closed.</div>
                  </div>
                </div>
                <EnablePushButton variant="outline" showTest={true} />
              </div>
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                <div className="flex items-center gap-3 min-w-0">
                  <Bell className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="text-sm">Smart nudges</div>
                    <div className="text-xs text-muted-foreground">Daily 8pm reminder if you haven&apos;t logged anything — helps keep your streak alive.</div>
                  </div>
                </div>
                <Switch checked={nudgesEnabled} onCheckedChange={toggleNudges} data-testid="settings-nudges-switch" />
              </div>
            </div>
          </section>
          {/* Help */}
          <section className="rounded-2xl border border-border bg-card/40 p-6 space-y-4" data-testid="settings-help">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Help</div>
            <button
              onClick={() => setGuideOpen(true)}
              className="w-full flex items-center justify-between gap-3 p-3 -m-3 rounded-lg hover:bg-foreground/[0.03] transition text-left"
              data-testid="settings-howto-row"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-9 w-9 rounded-lg bg-foreground/5 flex items-center justify-center shrink-0">
                  <BookOpen className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium">How to use Split the ____</div>
                  <div className="text-xs text-muted-foreground">6-step interactive walkthrough</div>
                </div>
              </div>
              <span className="text-xs font-mono uppercase tracking-wider brand-text shrink-0">Open</span>
            </button>
            <div className="pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">Install on your phone</div>
                <div className="text-xs text-muted-foreground mt-0.5">Add to your home screen — no App Store needed. Works offline.</div>
              </div>
              <InstallAppButton />
            </div>
          </section>

          {/* Danger Zone */}
          <section className="rounded-2xl border border-red-500/30 bg-red-500/[0.03] p-6" data-testid="settings-danger-zone">
            <div className="flex items-center gap-2 mb-5">
              <AlertTriangle className="h-4 w-4 text-red-400" strokeWidth={1.5} />
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-red-400">{t("settings.danger_zone")}</div>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.5} /> {t("settings.start_fresh")}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t("settings.start_fresh_desc")}</div>
                </div>
                <Button onClick={() => setResetOpen(true)} variant="outline" className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 h-11 w-full sm:w-auto shrink-0" data-testid="settings-start-fresh-btn">
                  {t("settings.start_fresh")}
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border border-red-500/30 bg-red-500/5">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2"><Trash2 className="h-3.5 w-3.5 text-red-400" strokeWidth={1.5} /> {t("settings.delete_account")}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t("settings.delete_account_desc")}</div>
                </div>
                <Button onClick={() => setDeleteOpen(true)} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-11 w-full sm:w-auto shrink-0" data-testid="settings-delete-account-btn">
                  {t("settings.delete_account")}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Start Fresh confirm */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-amber-400">{t("settings.start_fresh")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("settings.reset_confirm_body")}</p>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Type <span className="font-mono brand-text">RESET</span> to confirm</Label>
              <Input value={resetText} onChange={(e) => setResetText(e.target.value.toUpperCase())} className="mt-1.5 bg-muted border-amber-500/30 text-base h-11" autoFocus data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => { setResetOpen(false); setResetText(""); }} className="border-border h-11 w-full sm:w-auto">{t("common.cancel")}</Button>
            <Button onClick={doReset} disabled={resetText !== "RESET" || resetting} className="bg-amber-500 text-black hover:bg-amber-600 h-11 w-full sm:flex-1" data-testid="reset-confirm-btn">
              {resetting ? "Resetting…" : t("settings.start_fresh")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-red-400">{t("settings.delete_account")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t("settings.delete_confirm_body")}</p>
            <div>
              <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Type <span className="font-mono text-red-400">DELETE</span> to confirm</Label>
              <Input value={deleteText} onChange={(e) => setDeleteText(e.target.value.toUpperCase())} className="mt-1.5 bg-muted border-red-500/30 text-base h-11" autoFocus data-testid="delete-confirm-input" />
            </div>
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteText(""); }} className="border-border h-11 w-full sm:w-auto">{t("common.cancel")}</Button>
            <Button onClick={doDelete} disabled={deleteText !== "DELETE" || deletingAcct} className="bg-red-500 text-white hover:bg-red-600 h-11 w-full sm:flex-1" data-testid="delete-confirm-btn">
              {deletingAcct ? "Deleting…" : t("settings.delete_account")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <UserGuide open={guideOpen} onOpenChange={setGuideOpen} />
    </div>
  );
}
